import { useEffect, useRef, useState } from 'react';

interface UseChartWideDropZoneOptions {
  enabled?: boolean;
  onDrop: (files: File[]) => void;
}

interface UseChartWideDropZoneResult {
  /** True while a file drag is hovering over the window. */
  isDragging: boolean;
}

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  file?: (callback: (file: File) => void, errorCallback?: () => void) => void;
  createReader?: () => {
    readEntries: (
      callback: (entries: FileSystemEntryLike[]) => void,
      errorCallback?: () => void,
    ) => void;
  };
}

const MAX_DROPPED_FILES = 100;
const MAX_DIRECTORY_DEPTH = 20;

interface TraversalBudget {
  remainingFiles: number;
}

function readFileEntry(entry: FileSystemEntryLike, budget: TraversalBudget): Promise<File[]> {
  if (budget.remainingFiles <= 0) return Promise.resolve([]);
  return new Promise(resolve => {
    entry.file?.(
      file => {
        if (budget.remainingFiles <= 0) {
          resolve([]);
          return;
        }
        budget.remainingFiles -= 1;
        resolve([file]);
      },
      () => resolve([]),
    );
  });
}

async function readDirectoryEntry(
  entry: FileSystemEntryLike,
  budget: TraversalBudget,
  depth: number,
): Promise<File[]> {
  if (depth >= MAX_DIRECTORY_DEPTH || budget.remainingFiles <= 0) return [];
  const reader = entry.createReader?.();
  if (!reader) return [];

  const files: File[] = [];
  while (budget.remainingFiles > 0) {
    const batch = await new Promise<FileSystemEntryLike[]>(resolve => {
      reader.readEntries(resolve, () => resolve([]));
    });
    if (batch.length === 0) break;
    for (const child of batch) {
      files.push(...(await readEntry(child, budget, depth + 1)));
      if (budget.remainingFiles <= 0) break;
    }
  }
  return files;
}

function readEntry(
  entry: FileSystemEntryLike,
  budget: TraversalBudget,
  depth = 0,
): Promise<File[]> {
  if (entry.isFile) return readFileEntry(entry, budget);
  if (entry.isDirectory) return readDirectoryEntry(entry, budget, depth);
  return Promise.resolve([]);
}

/**
 * Return every file represented by a drop, recursively traversing directories
 * in browsers that expose the File System Entries API. Falls back to the flat
 * FileList in Firefox and other browsers without directory-entry support.
 */
export async function extractDroppedFiles(dataTransfer: DataTransfer): Promise<File[]> {
  const items = Array.from(dataTransfer.items ?? []);
  const entries: FileSystemEntryLike[] = [];
  for (const item of items) {
    const getEntry = (
      item as unknown as {
        webkitGetAsEntry?: () => FileSystemEntryLike | null;
      }
    ).webkitGetAsEntry;
    const entry = getEntry?.call(item);
    if (entry) entries.push(entry);
  }

  if (entries.length === 0) return Array.from(dataTransfer.files ?? []).slice(0, MAX_DROPPED_FILES);
  const budget: TraversalBudget = { remainingFiles: MAX_DROPPED_FILES };
  const files: File[] = [];
  for (const entry of entries) {
    files.push(...(await readEntry(entry, budget)));
    if (budget.remainingFiles <= 0) break;
  }
  return files;
}

/**
 * Attach window-level drag/drop listeners and track drag state.
 *
 * Uses a counter ref to survive the enter/leave churn that fires as the
 * cursor crosses child-element boundaries (a flat `isDragging` boolean
 * would flicker).
 *
 * Listeners are removed on unmount, so this hook is safe to own from a
 * component that only mounts conditionally (e.g. inside a tab).
 */
export function useChartWideDropZone({
  enabled = true,
  onDrop,
}: UseChartWideDropZoneOptions): UseChartWideDropZoneResult {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const onDropRef = useRef(onDrop);

  // Keep the latest onDrop in a ref so we don't reattach listeners on every
  // render (caller may not memoize the handler).
  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  useEffect(() => {
    if (!enabled) return;

    const hasFiles = (e: DragEvent): boolean =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const handleDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragCounter.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };

    const handleDragLeave = () => {
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      const dataTransfer = e.dataTransfer;
      if (!dataTransfer) return;
      void extractDroppedFiles(dataTransfer).then(files => {
        if (files.length > 0) onDropRef.current(files);
      });
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      // Reset in case we unmount mid-drag
      dragCounter.current = 0;
      setIsDragging(false);
    };
  }, [enabled]);

  return { isDragging };
}
